import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { TableLoader } from "../../../Common/ShimmerLoader";
import { GetAttendanceWards } from "../AttendanceApis";

const applyButtonStyle = {
  backgroundColor: "var(--btn-primary-bg)",
  color: "var(--btn-primary-text)",
  border: "none",
  fontWeight: 600,
  padding: "8px 24px",
  borderRadius: "6px",
  fontSize: "14px",
};

const resetButtonStyle = {
  backgroundColor: "#FDE8E8",
  color: "#8B1A1A",
  border: "none",
  fontWeight: 600,
  padding: "8px 24px",
  borderRadius: "6px",
  fontSize: "14px",
};

const DESIGNATION_OPTIONS = [
  { value: "Helper", label: "Helper" },
  { value: "Driver", label: "Driver" },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
const HEADER_BG = "#004554";
const HEADER_BORDER = "1px solid rgba(255, 255, 255, 0.45)";

const SAMPLE_REPORT_DATA = [
  {
    wardName: "Ward 1",
    plannedHelper: 10,
    actualHelpers: 15,
    percentageOfHelper: 150,
    plannedDriver: 8,
    actualDriver: 6,
    percentageOfDriver: 75,
    overallPercentage: 112.5,
    plannedVehicles: 5,
    deployedVehicleCount: 4,
    timeBefore630: 3,
    timeAfter630: 2,
    timeAfter700: 1,
  },
  {
    wardName: "Ward 2",
    plannedHelper: 12,
    actualHelpers: 10,
    percentageOfHelper: 83.3,
    plannedDriver: 7,
    actualDriver: 7,
    percentageOfDriver: 100,
    overallPercentage: 91.7,
    plannedVehicles: 6,
    deployedVehicleCount: 5,
    timeBefore630: 4,
    timeAfter630: 1,
    timeAfter700: 0,
  },
  {
    wardName: "Ward 3",
    plannedHelper: 9,
    actualHelpers: 9,
    percentageOfHelper: 100,
    plannedDriver: 5,
    actualDriver: 4,
    percentageOfDriver: 80,
    overallPercentage: 90,
    plannedVehicles: 4,
    deployedVehicleCount: 4,
    timeBefore630: 2,
    timeAfter630: 3,
    timeAfter700: 1,
  },
  {
    wardName: "Ward 12",
    plannedHelper: 18,
    actualHelpers: 16,
    percentageOfHelper: 88.9,
    plannedDriver: 11,
    actualDriver: 10,
    percentageOfDriver: 90.9,
    overallPercentage: 89.7,
    plannedVehicles: 10,
    deployedVehicleCount: 9,
    timeBefore630: 8,
    timeAfter630: 3,
    timeAfter700: 1,
  },
];

const tableHeadTh = {
  padding: "10px 12px",
  textAlign: "center",
  fontWeight: 600,
  fontSize: "12px",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  borderRight: HEADER_BORDER,
  borderBottom: HEADER_BORDER,
  background: HEADER_BG,
  color: "#fff",
};

const tableBodyTd = {
  padding: "8px 12px",
  color: "#374151",
  fontSize: "12px",
  whiteSpace: "nowrap",
  textAlign: "center",
  borderRight: "1px solid #e5e7eb",
  borderBottom: "1px solid #e5e7eb",
};

const buildTotals = (rows) => {
  if (!rows.length) return null;
  const sum = (key) => rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
  const plannedHelper = sum("plannedHelper");
  const actualHelpers = sum("actualHelpers");
  const plannedDriver = sum("plannedDriver");
  const actualDriver = sum("actualDriver");
  return {
    plannedHelper,
    actualHelpers,
    percentageOfHelper:
      plannedHelper > 0
        ? Math.round((actualHelpers / plannedHelper) * 1000) / 10
        : 0,
    plannedDriver,
    actualDriver,
    percentageOfDriver:
      plannedDriver > 0
        ? Math.round((actualDriver / plannedDriver) * 1000) / 10
        : 0,
    overallPercentage:
      plannedHelper + plannedDriver > 0
        ? Math.round(
            ((actualHelpers + actualDriver) / (plannedHelper + plannedDriver)) *
              1000
          ) / 10
        : 0,
    plannedVehicles: sum("plannedVehicles"),
    deployedVehicleCount: sum("deployedVehicleCount"),
    timeBefore630: sum("timeBefore630"),
    timeAfter630: sum("timeAfter630"),
    timeAfter700: sum("timeAfter700"),
  };
};

const PrimaryManPower = () => {
  const [wardOptions, setWardOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(SAMPLE_REPORT_DATA);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterData, setFilterData] = useState({
    WardId: "",
    Designation: "",
    FromDate: "",
    ToDate: "",
  });

  useEffect(() => {
    const fetchWards = async () => {
      try {
        const wards = await GetAttendanceWards();
        setWardOptions(Array.isArray(wards) ? wards : wards?.data ?? []);
      } catch (error) {
        console.error("Error fetching wards:", error?.message ?? error);
        setWardOptions([]);
      }
    };
    fetchWards();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      let rows = [...SAMPLE_REPORT_DATA];
      if (filterData.WardId) {
        const selectedWard = wardOptions.find(
          (w) =>
            String(w.circleId ?? w.wardId ?? w.CircleId) ===
            String(filterData.WardId)
        );
        const wardName =
          selectedWard?.circleName ??
          selectedWard?.wardName ??
          selectedWard?.wardNo ??
          "";
        if (wardName) {
          const matched = rows.filter(
            (row) =>
              String(row.wardName).toLowerCase() ===
              String(wardName).toLowerCase()
          );
          rows =
            matched.length > 0
              ? matched
              : [{ ...SAMPLE_REPORT_DATA[0], wardName }];
        }
      }
      setReportData(rows);
      setCurrentPage(1);
      setIsLoading(false);
    }, 300);
  };

  const handleReset = () => {
    setFilterData({
      WardId: "",
      Designation: "",
      FromDate: "",
      ToDate: "",
    });
    setReportData(SAMPLE_REPORT_DATA);
    setCurrentPage(1);
  };

  const totals = useMemo(() => buildTotals(reportData), [reportData]);

  const totalRecords = reportData.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));
  const paginatedRows = reportData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    return range;
  };

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">Attendance Primary Man Power</h5>
            <p className="text-muted mb-0">
              View primary manpower deployment by ward — helpers, drivers,
              vehicles, and reporting time bands.
            </p>
          </div>
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
            <form onSubmit={handleApplyFilters}>
              <Row className="align-items-end g-3">
                <Col xs="12" md="6" lg="3">
                  <label className="form-label">Ward</label>
                  <select
                    name="WardId"
                    className="form-select bg-white"
                    value={filterData.WardId}
                    onChange={handleChange}
                  >
                    <option value="">Select Ward</option>
                    {wardOptions.map((w) => (
                      <option
                        key={w.circleId ?? w.wardId ?? w.CircleId}
                        value={w.circleId ?? w.wardId ?? w.CircleId}
                      >
                        {w.circleName ?? w.wardName ?? w.wardNo}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs="12" md="6" lg="3">
                  <label className="form-label">Designation</label>
                  <select
                    name="Designation"
                    className="form-select bg-white"
                    value={filterData.Designation}
                    onChange={handleChange}
                  >
                    <option value="">Select Designation</option>
                    {DESIGNATION_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs="12" md="6" lg="3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    name="FromDate"
                    className="form-control bg-white"
                    value={filterData.FromDate}
                    onChange={handleChange}
                  />
                </Col>

                <Col xs="12" md="6" lg="3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    name="ToDate"
                    className="form-control bg-white"
                    value={filterData.ToDate}
                    min={filterData.FromDate || undefined}
                    onChange={handleChange}
                  />
                </Col>

                <Col xs="12" className="d-flex flex-wrap gap-2 mt-1">
                  <button type="submit" style={applyButtonStyle}>
                    APPLY FILTERS
                  </button>
                  <button
                    type="button"
                    style={resetButtonStyle}
                    onClick={handleReset}
                  >
                    RESET
                  </button>
                </Col>
              </Row>
            </form>
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body p-0">

            {isLoading ? (
              <div className="p-3">
                <TableLoader />
              </div>
            ) : (
              <>
                <div
                  style={{
                    overflowX: "auto",
                    maxHeight: "520px",
                    overflowY: "auto",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      borderSpacing: 0,
                      fontSize: "12px",
                      minWidth: "1300px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th rowSpan={2} style={tableHeadTh}>
                          S.No
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Ward Name
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Planned Helper
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Actual Helpers
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Percentage of Helper
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Planned Driver
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Actual Driver
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Percentage of Driver
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Over All Percentage
                        </th>
                        <th colSpan={2} style={tableHeadTh}>
                          Transport vehicle info
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Time Before 6.30
                        </th>
                        <th rowSpan={2} style={tableHeadTh}>
                          Time After 6.30
                        </th>
                        <th
                          rowSpan={2}
                          style={{ ...tableHeadTh, borderRight: "none" }}
                        >
                          Time After 7.00
                        </th>
                      </tr>
                      <tr>
                        <th style={tableHeadTh}>Planned</th>
                        <th style={tableHeadTh}>Deployed vehicle count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={14}
                            style={{
                              ...tableBodyTd,
                              textAlign: "center",
                              padding: "30px",
                              color: "#9ca3af",
                              borderRight: "none",
                            }}
                          >
                            No data found. Apply filters to view the report.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {paginatedRows.map((row, index) => {
                            const serialNo =
                              (currentPage - 1) * rowsPerPage + index + 1;
                            const rowBg =
                              index % 2 === 0 ? "#ffffff" : "#f9fafb";
                            return (
                              <tr
                                key={`${row.wardName}-${serialNo}`}
                                style={{ background: rowBg }}
                              >
                                <td style={tableBodyTd}>{serialNo}</td>
                                <td style={tableBodyTd}>{row.wardName}</td>
                                <td style={tableBodyTd}>{row.plannedHelper}</td>
                                <td style={tableBodyTd}>{row.actualHelpers}</td>
                                <td style={tableBodyTd}>
                                  {row.percentageOfHelper}
                                </td>
                                <td style={tableBodyTd}>{row.plannedDriver}</td>
                                <td style={tableBodyTd}>{row.actualDriver}</td>
                                <td style={tableBodyTd}>
                                  {row.percentageOfDriver}
                                </td>
                                <td style={tableBodyTd}>
                                  {row.overallPercentage}
                                </td>
                                <td style={tableBodyTd}>
                                  {row.plannedVehicles}
                                </td>
                                <td style={tableBodyTd}>
                                  {row.deployedVehicleCount}
                                </td>
                                <td style={tableBodyTd}>{row.timeBefore630}</td>
                                <td style={tableBodyTd}>{row.timeAfter630}</td>
                                <td
                                  style={{
                                    ...tableBodyTd,
                                    borderRight: "none",
                                  }}
                                >
                                  {row.timeAfter700}
                                </td>
                              </tr>
                            );
                          })}
                          {totals && (
                            <tr style={{ background: "#f1f5f9" }}>
                              <td
                                colSpan={2}
                                style={{ ...tableBodyTd, fontWeight: 700 }}
                              >
                                Total
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.plannedHelper}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.actualHelpers}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.percentageOfHelper}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.plannedDriver}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.actualDriver}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.percentageOfDriver}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.overallPercentage}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.plannedVehicles}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.deployedVehicleCount}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.timeBefore630}
                              </td>
                              <td style={{ ...tableBodyTd, fontWeight: 600 }}>
                                {totals.timeAfter630}
                              </td>
                              <td
                                style={{
                                  ...tableBodyTd,
                                  fontWeight: 600,
                                  borderRight: "none",
                                }}
                              >
                                {totals.timeAfter700}
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className="d-flex flex-wrap justify-content-between align-items-center gap-3 px-3 py-3"
                  style={{ borderTop: "1px solid #e5e7eb", fontSize: "12px" }}
                >
                  <span style={{ color: "#6b7280" }}>
                    Showing{" "}
                    {totalRecords === 0
                      ? 0
                      : (currentPage - 1) * rowsPerPage + 1}
                    –{Math.min(currentPage * rowsPerPage, totalRecords)} of{" "}
                    {totalRecords} results
                  </span>

                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                        Row per page
                      </span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "65px" }}
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        {ROWS_PER_PAGE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          padding: "2px 8px",
                          fontSize: "12px",
                          border: `1px solid ${HEADER_BG}`,
                          color: HEADER_BG,
                          background: "#fff",
                        }}
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                      >
                        ‹
                      </button>

                      {getPaginationRange().map((page) => (
                        <button
                          type="button"
                          key={page}
                          className="btn btn-sm"
                          style={{
                            padding: "2px 9px",
                            fontSize: "12px",
                            background:
                              page === currentPage ? HEADER_BG : "#FFFFFF",
                            color: page === currentPage ? "#fff" : HEADER_BG,
                            border: `1px solid ${HEADER_BG}`,
                            borderRadius: "4px",
                            minWidth: "30px",
                          }}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          padding: "2px 8px",
                          fontSize: "12px",
                          border: `1px solid ${HEADER_BG}`,
                          color: HEADER_BG,
                          background: "#fff",
                        }}
                        disabled={currentPage === totalPages || totalRecords === 0}
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimaryManPower;
